import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create, Delete } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { EntityInput } from "@dzambalaorg/mui-inputs-entity";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { emptyStateString } from "@dzambalaorg/draft-js-utils";
import { emptyItem, emptyPrice } from "@dzambalaorg/mui-inputs-asset";
import { useDialogDelete, useDialogForm, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { ListAction, ListActions, ListItem, StyledPagination } from "@framework/styled";
import { ILootBox, ITemplate, LootBoxStatus, ModuleType, TokenType } from "@framework/types";
import { LootBoxMintButton, WithCheckPermissionsListWrapper } from "@framework/shared";
import { Button, List } from "@dzambalaorg/ui-kit";

import { LootBoxDialogForm } from "./form";

const LOOT_BOXES_DIALOG_KEY = "lootBoxes";
const LOOT_BOXES_URL = "/loot/boxes";
const emptyValues = {
  title: "",
  description: emptyStateString,
  content: emptyItem,
  template: {
    price: emptyPrice,
  } as ITemplate,
};

export const LootBox = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { data, isLoading, isError, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } =
    useFetchQuery<IPaginationResult<ILootBox>>(LOOT_BOXES_URL, { enableInfiniteScroll: isMd }, void 0, {
      lootBoxStatus: [LootBoxStatus.ACTIVE],
    });

  const { handleForm, mode, selectedItem, formDialogUniqueKey } = useDialogForm<ILootBox>();
  const { handleDelete } = useDialogDelete<ILootBox>();

  const { address: account = "" } = useAccount();

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "loot", "loot.boxes"]} />

      <PageHeader message="pages.loot.boxes.title">
        <Button
          intent="secondary"
          startIcon={<Add />}
          onClick={handleForm(LOOT_BOXES_DIALOG_KEY, emptyValues, "create")}
          data-testid="LootBoxCreateButton"
        >
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <SearchWithFilters initialValues={{ lootBoxStatus: [LootBoxStatus.ACTIVE] }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 6 }}>
            <EntityInput
              name="contractIds"
              controller="contracts"
              multiple
              fetchData={{
                contractType: [TokenType.ERC721],
                contractModule: [ModuleType.LOOT],
              }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SelectInput multiple name="lootBoxStatus" options={LootBoxStatus} />
          </Grid>
        </Grid>
      </SearchWithFilters>

      <WithCheckPermissionsListWrapper
        isLoading={!data && isLoading}
        isError={!data && isError}
        isEmpty={allRows.length === 0}
        count={allRows.length}
      >
        {allRows.map(loot => (
          <ListItem key={loot.id} account={account} contract={loot.template?.contract}>
            <List.ItemText>{loot.title}</List.ItemText>
            <ListActions>
              <ListAction
                onClick={handleForm(LOOT_BOXES_DIALOG_KEY, loot, "edit")}
                message="form.buttons.edit"
                dataTestId="LootEditButton"
                icon={Create}
              />
              <ListAction
                onClick={handleDelete(loot)}
                message="form.buttons.delete"
                dataTestId="LootDeleteButton"
                icon={Delete}
                disabled={loot.lootBoxStatus === LootBoxStatus.INACTIVE}
              />
              <LootBoxMintButton loot={loot} disabled={loot.lootBoxStatus === LootBoxStatus.INACTIVE} />
            </ListActions>
          </ListItem>
        ))}
      </WithCheckPermissionsListWrapper>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={LOOT_BOXES_URL} refetch={refetch} />

      <LootBoxDialogForm
        open={formDialogUniqueKey === `${LOOT_BOXES_DIALOG_KEY}.${mode}`}
        mode={mode}
        selectedItem={selectedItem}
        url={LOOT_BOXES_URL}
        refetch={refetch}
      />
    </Grid>
  );
};
